/**
 * import-records.js
 *
 * Manages the "Import Records" workflow:
 * 1. File selection
 * 2. Upload & preview (one tab per sheet)
 * 3. Editable, scrollable tables with:
 *    • A sticky first data-column
 *    • A sticky header row
 *    • A right-pinned "Action" column of Save/Update buttons
 * 4. Bulk‐save or per‐row save/update (active sheet only)
 *
 * Best practices:
 * - Idempotent init to avoid dupes.
 * - Clear prior UI state before re-rendering.
 * - Filter out internal "action" key so it never shows up as raw data.
 * - Use descriptive variable names.
 * - Keep concerns separated: upload, render, gather, save.
 */

import { createLogger } from './debug';

const { warn } = createLogger('import-records');

let importRecordsInitialized = false;

/** @internal Test helper — reset init guard so initImportRecords() can run again. */
export function resetImportRecordsState() {
    importRecordsInitialized = false;
}

/**
 * Show a dismissable notice that auto-fades after 6 seconds
 * @param {string} message - The message to display
 * @param {string} type - 'success', 'error', 'warning', or 'info'
 */
function showNotice(message, type = 'info') {
    // Find or create notice container
    let container = document.getElementById('import-notices-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'import-notices-container';
        container.style.position = 'fixed';
        container.style.top = '20px';
        container.style.right = '20px';
        container.style.zIndex = '9999';
        container.style.maxWidth = '500px';
        document.body.appendChild(container);
    }

    // Create notice element
    const notice = document.createElement('div');
    notice.className = `callout ${type === 'error' ? 'alert' : type}`;
    notice.style.marginBottom = '10px';
    notice.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    notice.style.animation = 'slideInRight 0.3s ease-out';

    // Message text
    const messageEl = document.createElement('p');
    messageEl.style.margin = '0';
    messageEl.style.paddingRight = '2rem';
    messageEl.textContent = message;
    notice.appendChild(messageEl);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'close-button';
    closeBtn.setAttribute('aria-label', 'Dismiss alert');
    closeBtn.type = 'button';
    closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    closeBtn.style.position = 'absolute';
    closeBtn.style.right = '0.5rem';
    closeBtn.style.top = '50%';
    closeBtn.style.transform = 'translateY(-50%)';
    closeBtn.style.fontSize = '2rem';
    closeBtn.style.lineHeight = '1';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.background = 'transparent';
    closeBtn.style.border = 'none';
    closeBtn.style.padding = '0';
    closeBtn.style.color = 'inherit';
    notice.appendChild(closeBtn);

    // Add to container
    container.appendChild(notice);

    // Auto-dismiss after 6 seconds
    const timeout = setTimeout(() => {
        dismissNotice(notice);
    }, 6000);

    // Manual dismiss
    closeBtn.addEventListener('click', () => {
        clearTimeout(timeout);
        dismissNotice(notice);
    });
}

/**
 * Dismiss a notice with fade-out animation
 */
function dismissNotice(notice) {
    notice.style.animation = 'fadeOut 0.3s ease-out';
    notice.style.opacity = '0';
    setTimeout(() => {
        notice.remove();
    }, 300);
}

export function initImportRecords() {
    if (importRecordsInitialized) return;  // only run once
    importRecordsInitialized = true;

    const selectButton     = document.getElementById('select-file-button');
    const uploadButton     = document.getElementById('upload-file-button');
    const saveAllButton    = document.getElementById('save-records-button');
    const fileInputElement = document.getElementById('file-input');
    const fileNameDisplay  = document.getElementById('file-name');
    const previewContainer = document.getElementById('import-preview');

    if (!selectButton || !uploadButton || !fileInputElement || !previewContainer) {
        warn('missing DOM elements');
        return;
    }

    // 1) Open the file dialog
    selectButton.addEventListener('click', () => fileInputElement.click());

    // 2) Handle file selection
    fileInputElement.addEventListener('change', (e) => {
        previewContainer.innerHTML = '';     // clear previous preview/errors
        fileNameDisplay.style.color = '';    // reset text color

        const files = e.target.files;
        if (files && files.length > 0) {
            fileNameDisplay.innerHTML = `<strong>File:</strong>&nbsp;${files[0].name}`;
            uploadButton.hidden     = false;
            uploadButton.disabled   = false;
            uploadButton.textContent = 'Upload';
        } else {
            fileNameDisplay.textContent = 'No file selected';
            uploadButton.disabled = true;
        }
    });

    // 3) Upload + Render Preview
    uploadButton.addEventListener('click', async () => {
        const ok = await handleUpload();
        if (ok) {
            saveAllButton.hidden = false;  // now allow bulk‐save
            uploadButton.hidden  = true;   // force new file-select step
        }
        // on error, handleUpload already showed message & hid saveAllButton
    });

    // 4) Bulk save active sheet rows only
    if (saveAllButton) {
        saveAllButton.addEventListener('click', handleSave);
    }
}

/**
 * POST the file to /preview, then render the tabs & tables.
 * @returns {Promise<boolean>} success?
 */
export async function handleUpload() {
    const fileInputElement = document.getElementById('file-input');
    const uploadBtn = document.getElementById('upload-file-button');
    const previewContainer = document.getElementById('import-preview');
    const saveAllButton    = document.getElementById('save-records-button');

    let spinnerElement = null;
    if(uploadBtn) {
        uploadBtn.disabled = true;
        spinnerElement = document.createElement('span');
        spinnerElement.className = 'upload-spinner';
        uploadBtn.appendChild(spinnerElement);
    }

    const file = fileInputElement.files?.[0];
    if (!file) return false;

    const formData = new FormData();
    formData.append('spreadsheet', file);

    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const response = await fetch('/Admin/import/preview', {
        method: 'POST',
        headers: {
            'X-CSRF-TOKEN': csrfToken || ''
        },
        body: formData
    });

    if (!response.ok) {
        let errorMessage = 'Error uploading file';
        try {
            const errorData = await response.json();
            if (errorData.error) {
                errorMessage = errorData.error;
            }
        } catch (e) {
            // If JSON parsing fails, use status text
            errorMessage = `Error uploading file: ${response.statusText} (${response.status})`;
        }

        console.error('Upload error:', errorMessage, response);
        previewContainer.textContent = errorMessage;
        previewContainer.style.color = 'darkred';
        if (saveAllButton) saveAllButton.hidden = true;

        // Remove spinner & re-enable button on error
        if (uploadBtn && spinnerElement) {
            uploadBtn.removeChild(spinnerElement);
            uploadBtn.disabled = false;
        }
        return false;
    }

    // Expected JSON shape:
    // { SheetName: { headers: [...], rows: [...] }, _meta: { columnStatus: {...} } }
    const sheetsMap = await response.json();
    const columnStatus = sheetsMap._meta?.columnStatus || {};
    renderPreviewTabs(previewContainer, sheetsMap, columnStatus);

    // Remove spinner & re-enable button
    if (uploadBtn && spinnerElement) {
        uploadBtn.removeChild(spinnerElement);
        uploadBtn.disabled = false;
    }
    return true;
}

/**
 * Build a tabbed UI—one tab per sheet, with a right-aligned totals display.
 * @param {HTMLElement} container
 * @param {Object<string,{headers:string[],rows:Object[]}>} sheetsMap
 * @param {Object<string,boolean>} columnStatus - Column existence status
 */
export function renderPreviewTabs(container, sheetsMap, columnStatus = {}) {
    container.innerHTML = '';

    // Column detection banner
    renderColumnStatusBanner(container, columnStatus);

    // Compute totals (exclude _meta key)
    const sheetNames = Object.keys(sheetsMap).filter(name => name !== '_meta');
    const sheetCounts = sheetNames.map(name => sheetsMap[name].rows.length);
    const totalAll = sheetCounts.reduce((sum, c) => sum + c, 0);

    // Totals display
    const totalsDiv = document.createElement('div');
    totalsDiv.id = 'preview-totals';
    totalsDiv.className = 'preview-totals';
    totalsDiv.style.textAlign = 'right';        // align right
    totalsDiv.style.marginBottom = '8px';       // spacing from tabs
    // start with first sheet active
    totalsDiv.innerHTML = `<strong>Totals: ${sheetCounts[0]} of ${totalAll}</strong>`;
    container.appendChild(totalsDiv);

    // Tab buttons
    const tabsNav = document.createElement('ul');
    tabsNav.className = 'tabs-nav';

    // Tab panels
    const tabsContent = document.createElement('div');
    tabsContent.className = 'tabs-content';

    let first = true;
    sheetNames.forEach((sheetName, idx) => {
        const { headers, rows } = sheetsMap[sheetName];
        const panelId = `import-tab-${idx}`;

        // Determine sheet metadata
        const sheetMeta = getSheetMetadata(sheetName);

        // --- Button ---
        const tabButton = document.createElement('li');
        tabButton.className = 'tab-item' + (first ? ' is-active' : '');

        // Add sheet name with badge
        const badge = document.createElement('span');
        badge.className = `sheet-badge ${sheetMeta.badgeClass}`;
        badge.textContent = sheetMeta.label;
        badge.style.marginLeft = '8px';
        badge.style.padding = '2px 8px';
        badge.style.borderRadius = '12px';
        badge.style.fontSize = '11px';
        badge.style.fontWeight = 'bold';

        tabButton.textContent = sheetName + ' ';
        tabButton.appendChild(badge);
        tabButton.dataset.target = panelId;
        tabButton.dataset.sheetName = sheetName;
        tabButton.dataset.count = rows.length;
        tabButton.dataset.ignored = sheetMeta.ignored;

        // Color-code tab based on type
        if (sheetMeta.ignored) {
            tabButton.style.opacity = '0.6';
            tabButton.style.fontStyle = 'italic';
        }

        tabsNav.appendChild(tabButton);

        // --- Panel ---
        const tabPanel = document.createElement('div');
        tabPanel.className = 'tab-panel' + (first ? ' is-active' : '');
        tabPanel.id = panelId;
        buildPreviewTable(tabPanel, headers, rows, panelId);
        tabsContent.appendChild(tabPanel);

        first = false;
    });

    // Switch tabs and update totals
    tabsNav.addEventListener('click', (e) => {
        const clicked = e.target.closest('.tab-item');
        if (!clicked) return;
        const targetId = clicked.dataset.target;

        tabsNav.querySelectorAll('.tab-item').forEach(item => item.classList.remove('is-active'));
        tabsContent.querySelectorAll('.tab-panel').forEach(panel => panel.classList.remove('is-active'));

        clicked.classList.add('is-active');
        document.getElementById(targetId)?.classList.add('is-active');

        // update totals
        const activeCount = parseInt(clicked.dataset.count, 10) || 0;
        totalsDiv.innerHTML = `<strong>Totals: ${activeCount} of ${totalAll}</strong>`;
    });

    container.appendChild(tabsNav);
    container.appendChild(tabsContent);
}

/**
 * Get sheet metadata based on sheet name.
 * @param {string} sheetName
 * @returns {{label: string, badgeClass: string, type: string, ignored: boolean}}
 */
function getSheetMetadata(sheetName) {
    const ignoredSheets = ['Comp Cancelled', 'Subscriber cancelled'];
    const compSheets = ['Comp Active'];
    const paidSheets = ['USA Active', 'Overseas Active'];

    if (ignoredSheets.includes(sheetName)) {
        return {
            label: 'IGNORED',
            badgeClass: 'badge-ignored',
            type: 'ignored',
            ignored: true
        };
    } else if (compSheets.includes(sheetName)) {
        return {
            label: 'FREE',
            badgeClass: 'badge-free',
            type: 'free',
            ignored: false
        };
    } else if (paidSheets.includes(sheetName)) {
        return {
            label: 'PAID',
            badgeClass: 'badge-paid',
            type: 'paid',
            ignored: false
        };
    } else {
        return {
            label: 'UNKNOWN',
            badgeClass: 'badge-unknown',
            type: 'unknown',
            ignored: false
        };
    }
}

/**
 * Display column status banner showing which optional columns exist.
 * @param {HTMLElement} container
 * @param {Object<string,boolean>} columnStatus
 */
function renderColumnStatusBanner(container, columnStatus) {
    const banner = document.createElement('div');
    banner.className = 'column-status-banner';
    banner.style.padding = '12px';
    banner.style.marginBottom = '16px';
    banner.style.borderRadius = '4px';
    banner.style.border = '1px solid';

    // Check if all columns exist
    const allExist = Object.values(columnStatus).every(exists => exists);
    const missingColumns = Object.entries(columnStatus)
        .filter(([_, exists]) => !exists)
        .map(([name]) => name);

    if (allExist) {
        // Success: all columns exist
        banner.style.backgroundColor = '#d4edda';
        banner.style.borderColor = '#c3e6cb';
        banner.style.color = '#155724';
        banner.innerHTML = `
            <strong>✓ All required columns detected</strong><br>
            <small>Credits, Name1/Name2, Notes, and LastValidated columns are available in the database.</small>
        `;
    } else {
        // Warning: some columns missing
        banner.style.backgroundColor = '#fff3cd';
        banner.style.borderColor = '#ffeaa7';
        banner.style.color = '#856404';

        const columnLabels = {
            'Credits': 'Credits (subscription issue credits)',
            'Name1': 'Name1 (organization name)',
            'Name2': 'Name2 (department/attention)',
            'UserNotes': 'User Notes',
            'SubscriptionNotes': 'Subscription Notes',
            'LastValidated': 'LastValidated (address validation tracking)'
        };

        const missingList = missingColumns
            .map(col => columnLabels[col] || col)
            .join(', ');

        banner.innerHTML = `
            <strong>⚠ Missing optional columns</strong><br>
            <small>The following columns don't exist yet: <strong>${missingList}</strong>.<br>
            Data for these fields will be skipped during import. Contact IT to run the database migration.</small>
        `;
    }

    container.appendChild(banner);
}

/**
 * Build one table:
 *  • filters out the raw "action" data column
 *  • freezes first column and header row
 *  • pins the rightmost Action button column
 *  • sorts rows to show "save" before "update" (already imported at bottom)
 */
function buildPreviewTable(parentElement, headersArray, rowsArray, panelId) {
    // Only real data columns—remove the server's "action" field
    const dataHeaders = headersArray
        .filter(headerName => headerName && typeof headerName === 'string')
        .filter(headerName => headerName.toLowerCase() !== 'action');

    // Sort rows: 'save' (new records) first, 'update' (already imported) last
    const sortedRows = [...rowsArray].sort((a, b) => {
        if (a.action === 'save' && b.action === 'update') return -1;
        if (a.action === 'update' && b.action === 'save') return 1;
        return 0; // maintain original order within same action type
    });

    // Scroll wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'preview-wrapper';

    const table = document.createElement('table');
    table.className = 'responsive-table import-preview-table';

    // HEADER ROW
    const thead = table.createTHead();
    const headerRow = thead.insertRow();
    dataHeaders.forEach((colName, colIndex) => {
        const th = document.createElement('th');
        th.textContent = colName;
        headerRow.appendChild(th);
    });
    // Add the button‐column header
    const thAction = document.createElement('th');
    thAction.textContent = 'Action';
    thAction.className = 'action-col';
    headerRow.appendChild(thAction);

    // BODY ROWS
    const tbody = table.createTBody();
    sortedRows.forEach((rowObject, rowIndex) => {
        const tr = tbody.insertRow();
        const isAlreadyImported = rowObject.action === 'update';

        // Data inputs or read-only display
        dataHeaders.forEach((fieldName, colIndex) => {
            const td = tr.insertCell();

            if (isAlreadyImported) {
                // Already imported: show as read-only text with grey background
                const span = document.createElement('span');
                span.textContent = rowObject[fieldName] ?? '';
                span.style.display = 'block';
                span.style.padding = '0.5rem';
                span.style.backgroundColor = '#f0f0f0';
                span.style.borderRadius = '3px';
                span.style.color = '#666';
                td.appendChild(span);
            } else {
                // New record: show as editable input
                const inputEl = document.createElement('input');
                inputEl.value = rowObject[fieldName] ?? '';
                inputEl.dataset.rowIndex = String(rowIndex);
                inputEl.dataset.field    = fieldName;
                td.appendChild(inputEl);
            }
        });

        // Action button
        const tdAction = tr.insertCell();
        tdAction.classList.add('action-col');

        if (isAlreadyImported) {
            // Show "Already Imported" label
            const label = document.createElement('span');
            label.textContent = 'Already Imported';
            label.style.color = '#666';
            label.style.fontStyle = 'italic';
            label.style.fontSize = '0.9rem';
            tdAction.appendChild(label);
        } else {
            // Show Save button for new records
            const buttonEl = document.createElement('button');
            buttonEl.textContent = 'Save';
            buttonEl.className = 'button small';
            buttonEl.dataset.panelId  = panelId;
            buttonEl.dataset.rowIndex = String(rowIndex);
            buttonEl.dataset.action   = 'save';
            buttonEl.addEventListener('click', () => handleSaveRow(panelId, rowIndex));
            tdAction.appendChild(buttonEl);
        }
    });

    wrapper.appendChild(table);
    parentElement.appendChild(wrapper);

    // Add "Import All" button below the table (centered)
    const btnContainer = document.createElement('div');
    btnContainer.style.textAlign = 'center';
    btnContainer.style.marginTop = '1rem';

    const importAllBtn = document.createElement('button');
    importAllBtn.textContent = 'Import All Records in This Sheet';
    importAllBtn.className = 'button primary';
    importAllBtn.dataset.panelId = panelId;
    importAllBtn.addEventListener('click', () => handleImportAllInSheet(panelId));

    btnContainer.appendChild(importAllBtn);
    parentElement.appendChild(btnContainer);

    // Add inline progress bar below the Import All button (visible during import)
    const inlineProgress = document.createElement('div');
    inlineProgress.id = `inline-progress-${panelId}`;
    inlineProgress.style.display = 'none';
    inlineProgress.style.marginTop = '1rem';
    inlineProgress.style.maxWidth = '500px';
    inlineProgress.style.marginLeft = 'auto';
    inlineProgress.style.marginRight = 'auto';
    inlineProgress.innerHTML = `
        <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100">
            <span class="progress-meter" style="width: 0%;"></span>
        </div>
        <p style="margin: 0.25rem 0 0; font-size: 0.85rem; color: #555; text-align: center;">Preparing import...</p>
    `;
    parentElement.appendChild(inlineProgress);

    // Add HR separator
    const hr = document.createElement('hr');
    hr.style.marginTop = '2rem';
    hr.style.marginBottom = '1rem';
    parentElement.appendChild(hr);

    // Add import summary container below the button
    const summaryContainer = document.createElement('div');
    summaryContainer.id = `import-summary-${panelId}`;
    summaryContainer.className = 'import-summary';
    summaryContainer.style.display = 'none'; // Hidden until import completes
    parentElement.appendChild(summaryContainer);
}

/**
 * Start polling import progress and update the inline progress bar.
 *
 * Returns a Promise that resolves with the final progress data
 * when the import completes (status === 'complete') or rejects
 * on error. The caller can await this to get the result.
 *
 * @param {string} panelId - The panel ID to find the inline progress bar
 * @returns {{promise: Promise<Object>, stop: Function}} Controller object
 */
function startProgressPolling(panelId) {
    const inlineContainer = document.getElementById(`inline-progress-${panelId}`);
    const meter = inlineContainer?.querySelector('.progress-meter');
    const text = inlineContainer?.querySelector('p');

    if (inlineContainer) {
        inlineContainer.style.display = 'block';
    }
    if (meter) {
        meter.style.width = '0%';
    }
    if (text) {
        text.textContent = 'Preparing import...';
        text.style.color = '#555';
    }

    let intervalId = null;
    let timeoutId = null;

    // Reset the no-progress watchdog; called on every successful progress update
    const NO_PROGRESS_TIMEOUT = 5 * 60 * 1000; // 5 minutes without any update = stalled
    function resetWatchdog() {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            clearInterval(intervalId);
            reject({ message: 'Import timed out — no progress from worker for 5 minutes' });
        }, NO_PROGRESS_TIMEOUT);
    }

    const promise = new Promise((resolve, reject) => {
        // Start the initial watchdog (worker hasn't written anything yet)
        resetWatchdog();

        intervalId = setInterval(async () => {
            try {
                const resp = await fetch('/Admin/import/progress');
                if (!resp.ok) return;

                const progress = await resp.json();
                if (!progress || progress.status === 'idle') return;

                if (progress.status === 'running' && progress.total > 0) {
                    resetWatchdog(); // still making progress — extend the deadline
                    const pct = Math.round((progress.current / progress.total) * 100);
                    if (meter) {
                        meter.style.width = pct + '%';
                    }
                    if (text) {
                        let msg = progress.current + ' of ' + progress.total + ' (' + pct + '%)';
                        if (progress.last_name) {
                            msg += ' \u2014 ' + progress.last_name;
                        }
                        text.textContent = msg;
                    }
                } else if (progress.status === 'complete') {
                    if (meter) {
                        meter.style.width = '100%';
                    }
                    if (text) {
                        text.textContent = 'Import complete!';
                    }
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                    setTimeout(() => {
                        if (inlineContainer) inlineContainer.style.display = 'none';
                    }, 2000);
                    resolve(progress);
                } else if (progress.status === 'error') {
                    if (text) {
                        text.textContent = 'Import error: ' + (progress.message || 'Unknown error');
                        text.style.color = '#cc4b37';
                    }
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                    reject(progress);
                }
            } catch (e) {
                // Polling error — silently ignore, import is still running
            }
        }, 20000);
    });

    return {
        promise,
        stop() {
            if (intervalId) {
                clearInterval(intervalId);
                clearTimeout(timeoutId);
                setTimeout(() => {
                    if (inlineContainer) inlineContainer.style.display = 'none';
                    if (text) text.style.color = '';
                }, 2000);
            }
        }
    };
}

/**
 * Import all records in a specific sheet (panel)
 */
async function handleImportAllInSheet(panelId) {
    const panel = document.getElementById(panelId);
    if (!panel) {
        showNotice('Sheet panel not found', 'error');
        return;
    }

    // Get sheet name from the panel's corresponding tab
    const allTabs = document.querySelectorAll('.tab-item');
    let sheetName = '';
    allTabs.forEach(tab => {
        if (tab.dataset.target === panelId) {
            sheetName = tab.dataset.sheetName || '';
        }
    });

    if (!sheetName) {
        showNotice('Could not determine sheet name', 'error');
        return;
    }

    // Disable button and show spinner
    const importBtn = panel.querySelector('.button.primary[data-panel-id]');
    if (importBtn) {
        importBtn.disabled = true;
        importBtn.dataset.originalText = importBtn.textContent;
        importBtn.textContent = 'Importing\u2026 ';
        const spinnerEl = document.createElement('i');
        spinnerEl.className = 'fa-solid fa-spinner fa-spin';
        importBtn.appendChild(spinnerEl);
    }

    // Gather all rows from this panel
    const inputs = Array.from(
        panel.querySelectorAll('input[data-row-index][data-field]')
    );
    const rowsMap = {};
    inputs.forEach(inputEl => {
        const r = parseInt(inputEl.dataset.rowIndex, 10);
        rowsMap[r] = rowsMap[r] || {};
        rowsMap[r][inputEl.dataset.field] = inputEl.value;
    });

    // Attach action to each row
    const buttons = Array.from(
        panel.querySelectorAll('button[data-row-index]')
    );
    buttons.forEach(btn => {
        const r = parseInt(btn.dataset.rowIndex, 10);
        if (rowsMap[r]) {
            rowsMap[r].action = btn.dataset.action || 'save';
        }
    });

    const rows = Object.keys(rowsMap)
        .map(k => parseInt(k, 10))
        .sort((a, b) => a - b)
        .map(idx => rowsMap[idx]);

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    // Start progress polling (returns a promise that resolves on complete)
    const polling = startProgressPolling(panelId);

    const response = await fetch('/Admin/import/save-all', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken || ''
        },
        body: JSON.stringify({ rows, sheetName })
    });

    if (!response.ok) {
        polling.stop();
        let errorMsg = 'Error importing records';
        try {
            const errorData = await response.json();
            if (errorData.error) {
                errorMsg = errorData.error;

                // Append technical details if available (dev/staging environments)
                if (errorData.technicalDetails) {
                    errorMsg += '\n\nTechnical Details:\n' + errorData.technicalDetails;
                    if (errorData.file && errorData.line) {
                        errorMsg += '\nLocation: ' + errorData.file + ':' + errorData.line;
                    }
                }
            }
        } catch (e) {
            // Ignore JSON parse error
        }
        showNotice(errorMsg, 'error');

        // Restore button on error
        if (importBtn) {
            importBtn.textContent = importBtn.dataset.originalText || 'Import All Records in This Sheet';
            importBtn.disabled = false;
        }
        return;
    }

    const responseData = await response.json();

    // If the server returned the result directly (sync mode), use it.
    // If it returned {started: true} (async mode), wait for polling to complete.
    let data;
    if (responseData.started) {
        try {
            data = await polling.promise;
        } catch (errorProgress) {
            showNotice('Import error: ' + (errorProgress.message || 'Unknown error'), 'error');
            if (importBtn) {
                importBtn.textContent = importBtn.dataset.originalText || 'Import All Records in This Sheet';
                importBtn.disabled = false;
            }
            return;
        }
    } else {
        polling.stop();
        data = responseData;
    }

    // Update button while building summary (re-upload + render takes a few seconds)
    if (importBtn) {
        importBtn.textContent = 'Building summary\u2026';
    }

    // Build summary HTML
    const totalSkipped = (data.skipped_duplicates || 0) + (data.skipped_existing || 0) + (data.skipped_invalid || 0);
    let summaryHTML = `
        <div class="callout ${data.totalFailed > 0 ? 'warning' : 'success'}">
            <h5>Import Summary for "${sheetName}"</h5>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-top: 1rem;">
                <div>
                    <strong style="color: #3adb76;">✓ Inserted:</strong> ${data.inserted || 0}<br>
                    <strong style="color: #3adb76;">✓ Updated:</strong> ${data.updated || 0}
                </div>`;

    if (totalSkipped > 0) {
        summaryHTML += `
                <div>
                    <strong style="color: #ffae00;">⊘ Skipped:</strong> ${totalSkipped}<br>
                    ${data.skipped_existing > 0 ? `<span style="font-size: 0.9em;">• Already imported: ${data.skipped_existing}</span><br>` : ''}
                    ${data.skipped_duplicates > 0 ? `<span style="font-size: 0.9em;">• Duplicates: ${data.skipped_duplicates}</span><br>` : ''}
                    ${data.skipped_invalid > 0 ? `<span style="font-size: 0.9em;">• Invalid: ${data.skipped_invalid}</span><br>` : ''}
                </div>`;
    }

    summaryHTML += `
                <div>
                    <strong>Total Success:</strong> ${data.totalSuccess || 0}
                    ${data.totalFailed > 0 ? `<br><strong style="color: #cc4b37;">✗ Failed:</strong> ${data.totalFailed}` : ''}
                </div>
            </div>`;

    // Skipped details accordion
    if (totalSkipped > 0 && data.skipped_details && data.skipped_details.length > 0) {
        const reasonLabels = { existing: 'Already imported', duplicate: 'Duplicate', invalid: 'Invalid (missing email)' };
        const detailRows = data.skipped_details.map(d => {
            const label = reasonLabels[d.reason] || d.reason;
            const ident = d.email ? d.email : (d.name || '\u2014');
            return `<tr>
                <td style="padding: 0.25rem 0.5rem;">${d.row}</td>
                <td style="padding: 0.25rem 0.5rem;">${label}</td>
                <td style="padding: 0.25rem 0.5rem;">${ident}</td>
                <td style="padding: 0.25rem 0.5rem; font-size: 0.85em; color: #666;">${d.detail || ''}</td>
            </tr>`;
        }).join('');

        summaryHTML += `
            <div style="margin-top: 1rem;">
                <a href="#" style="font-size: 0.9em;" onclick="event.preventDefault(); var el = this.nextElementSibling; el.style.display = el.style.display === 'none' ? 'block' : 'none'; this.textContent = el.style.display === 'none' ? 'Show skipped records ▸' : 'Hide skipped records ▾';">Show skipped records ▸</a>
                <div style="display: none; margin-top: 0.5rem; max-height: 300px; overflow-y: auto;">
                    <table style="width: 100%; font-size: 0.9em; border-collapse: collapse;">
                        <thead><tr style="border-bottom: 1px solid #ddd;">
                            <th style="padding: 0.25rem 0.5rem; text-align: left;">Row</th>
                            <th style="padding: 0.25rem 0.5rem; text-align: left;">Reason</th>
                            <th style="padding: 0.25rem 0.5rem; text-align: left;">Email / Name</th>
                            <th style="padding: 0.25rem 0.5rem; text-align: left;">Detail</th>
                        </tr></thead>
                        <tbody>${detailRows}</tbody>
                    </table>
                </div>
            </div>`;
    }

    if (data.totalFailed > 0 && data.errors && data.errors.length > 0) {
        summaryHTML += `
            <div style="margin-top: 1rem; max-height: 200px; overflow-y: auto;">
                <strong>Errors:</strong>
                <ul style="margin: 0.5rem 0 0 1rem; font-size: 0.9em;">
                    ${data.errors.slice(0, 10).map(e => `<li>Row ${e.row}: ${e.error}</li>`).join('')}
                    ${data.errors.length > 10 ? `<li><em>... and ${data.errors.length - 10} more errors</em></li>` : ''}
                </ul>
            </div>`;
    }

    summaryHTML += '</div>';

    // Reload the preview to update action buttons
    const fileInputElement = document.getElementById('file-input');
    if (fileInputElement.files?.[0]) {
        await handleUpload();
    }

    // Re-activate the tab that was just imported (handleUpload resets to first tab)
    const reloadedTab = document.querySelector(`.tab-item[data-target="${panelId}"]`);
    if (reloadedTab) {
        document.querySelectorAll('.tab-item').forEach(t => t.classList.remove('is-active'));
        document.querySelectorAll('.tab-panel').forEach(p => p.classList.remove('is-active'));
        reloadedTab.classList.add('is-active');
        document.getElementById(panelId)?.classList.add('is-active');

        // Update totals display
        const totalsDiv = document.getElementById('preview-totals');
        const activeCount = parseInt(reloadedTab.dataset.count, 10) || 0;
        const allCounts = Array.from(document.querySelectorAll('.tab-item'))
            .reduce((sum, t) => sum + (parseInt(t.dataset.count, 10) || 0), 0);
        if (totalsDiv) {
            totalsDiv.innerHTML = `<strong>Totals: ${activeCount} of ${allCounts}</strong>`;
        }
    }

    // Display summary in the container AFTER reload (so it doesn't get wiped)
    const summaryContainer = document.getElementById(`import-summary-${panelId}`);
    if (summaryContainer) {
        summaryContainer.innerHTML = summaryHTML;
        summaryContainer.style.display = 'block';
    }

    // Restore button and show final notice
    if (importBtn) {
        importBtn.textContent = importBtn.dataset.originalText || 'Import All Records in This Sheet';
        importBtn.disabled = false;
    }
    showNotice(`Import summary ready. ${data.totalSuccess || 0} records processed.`, 'success');
}

/**
 * Save or update one row.
 */
async function handleSaveRow(panelId, rowIndex) {
    const panel = document.getElementById(panelId);
    if (!panel) {
        console.error(`Panel ${panelId} not found`);
        return;
    }

    // Get sheet name from active tab
    const activeTab = document.querySelector('.tab-item.is-active');
    const sheetName = activeTab?.dataset.sheetName || '';
    if (!sheetName) {
        showNotice('Could not determine sheet name', 'error');
        return;
    }

    // Gather inputs for just this row
    const inputs = Array.from(
        panel.querySelectorAll(`input[data-row-index="${rowIndex}"]`)
    );
    const rowData = {};
    inputs.forEach(inputEl => {
        rowData[inputEl.dataset.field] = inputEl.value;
    });

    // Read the intended action from the button
    const btn = panel.querySelector(
        `button[data-row-index="${rowIndex}"]`
    );
    rowData.action = btn?.dataset.action || 'save';

    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const response = await fetch('/Admin/import/save-row', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken || ''
        },
        body: JSON.stringify({ row: rowData, sheetName })
    });

    if (!response.ok) {
        let errorMsg = 'Error saving row';
        try {
            const errorData = await response.json();
            if (errorData.error) {
                errorMsg = errorData.error;

                // Append technical details if available (dev/staging environments)
                if (errorData.technicalDetails) {
                    errorMsg += '\n\nTechnical Details:\n' + errorData.technicalDetails;
                    if (errorData.file && errorData.line) {
                        errorMsg += '\nLocation: ' + errorData.file + ':' + errorData.line;
                    }
                }
            }
            if (errorData.errors && errorData.errors.length > 0) {
                errorMsg += '\n' + errorData.errors.map(e => `Row ${e.row}: ${e.error}`).join('\n');
            }
        } catch (e) {
            // Ignore JSON parse error
        }
        showNotice(errorMsg, 'error');
    } else {
        showNotice('Row saved successfully', 'success');
    }
}

/**
 * Bulk‐save only inputs from the active sheet via the /save-all endpoint.
 */
export async function handleSave() {
    const saveAllButton = document.getElementById('save-records-button');
    saveAllButton.disabled = true;

    // Find active panel
    const activePanel = document.querySelector('.tab-panel.is-active');
    if (!activePanel) {
        showNotice('No active sheet to save', 'error');
        saveAllButton.disabled = false;
        return;
    }

    // Get sheet name from active tab
    const activeTab = document.querySelector('.tab-item.is-active');
    const sheetName = activeTab?.dataset.sheetName || '';
    if (!sheetName) {
        showNotice('Could not determine sheet name', 'error');
        saveAllButton.disabled = false;
        return;
    }

    // Gather data just from active panel
    const inputs = Array.from(
        activePanel.querySelectorAll('input[data-row-index][data-field]')
    );
    const rowsMap = {};
    inputs.forEach(inputEl => {
        const r = parseInt(inputEl.dataset.rowIndex, 10);
        rowsMap[r] = rowsMap[r] || {};
        rowsMap[r][inputEl.dataset.field] = inputEl.value;
    });
    const rows = Object.keys(rowsMap)
        .map(k => parseInt(k, 10))
        .sort((a, b) => a - b)
        .map(idx => rowsMap[idx]);

    // Also need to attach the action to each row
    const buttons = Array.from(
        activePanel.querySelectorAll('button[data-row-index]')
    );
    buttons.forEach(btn => {
        const r = parseInt(btn.dataset.rowIndex, 10);
        if (rowsMap[r]) {
            rowsMap[r].action = btn.dataset.action || 'save';
        }
    });

    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    // Start progress polling
    const polling = startProgressPolling(activePanel.id);

    const response = await fetch('/Admin/import/save-all', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken || ''
        },
        body: JSON.stringify({ rows, sheetName })
    });

    if (!response.ok) {
        polling.stop();
        let errorMsg = 'Error saving records';
        try {
            const errorData = await response.json();
            if (errorData.error) {
                errorMsg = errorData.error;

                // Append technical details if available (dev/staging environments)
                if (errorData.technicalDetails) {
                    errorMsg += '\n\nTechnical Details:\n' + errorData.technicalDetails;
                    if (errorData.file && errorData.line) {
                        errorMsg += '\nLocation: ' + errorData.file + ':' + errorData.line;
                    }
                }
            }
        } catch (e) {
            // Ignore JSON parse error
        }
        showNotice(errorMsg, 'error');
        saveAllButton.disabled = false;
        return;
    }

    const responseData = await response.json();

    // If async mode, wait for polling to deliver results
    let data;
    if (responseData.started) {
        try {
            data = await polling.promise;
        } catch (errorProgress) {
            showNotice('Import error: ' + (errorProgress.message || 'Unknown error'), 'error');
            saveAllButton.disabled = false;
            return;
        }
    } else {
        polling.stop();
        data = responseData;
    }

    let message = `Import complete!\n\n✓ Inserted: ${data.inserted || 0}\n✓ Updated: ${data.updated || 0}`;

    // Show skip statistics if any records were skipped
    const totalSkipped = (data.skipped_duplicates || 0) + (data.skipped_existing || 0) + (data.skipped_invalid || 0);
    if (totalSkipped > 0) {
        message += `\n\n⊘ Skipped: ${totalSkipped}`;
        if (data.skipped_existing > 0) {
            message += `\n  • Already imported: ${data.skipped_existing}`;
        }
        if (data.skipped_duplicates > 0) {
            message += `\n  • Duplicates in batch: ${data.skipped_duplicates}`;
        }
        if (data.skipped_invalid > 0) {
            message += `\n  • Invalid (missing email): ${data.skipped_invalid}`;
        }
    }

    message += `\n\nTotal Success: ${data.totalSuccess || 0}`;

    if (data.totalFailed > 0) {
        message += `\n✗ Failed: ${data.totalFailed}`;
        if (data.errors && data.errors.length > 0) {
            message += '\n\nErrors:\n' + data.errors.slice(0, 10).map(e => `Row ${e.row}: ${e.error}`).join('\n');
            if (data.errors.length > 10) {
                message += `\n... and ${data.errors.length - 10} more errors`;
            }
        }
    }
    showNotice(message, data.totalFailed > 0 ? 'warning' : 'success');
    // Reset UI
    document.getElementById('import-preview').innerHTML = '';
    document.getElementById('file-name').textContent    = 'No file selected';
    saveAllButton.hidden = true;
}

// Auto-init after DOM ready
if (typeof window !== 'undefined') {
    window.addEventListener('DOMContentLoaded', initImportRecords);
}
